import { ShowingDto } from '@cineamo/api/models/showing/ShowingDto.types';

import { CountryCode } from '@cineamo/api/types/CountryCode';

import { TMDB_IMAGE_RESOLUTION } from '@cineamo/api/constants/tmdb-constants';

import { getMovieAgeRating, MovieVersionDto } from '@cineamo/api/helpers/movie-helper';

import { getLanguageCodeFromString } from '@cineamo/ui/src/helpers/locale-helper';
import {
    getShowingBackdrop,
    getShowingBackdropBlurDataImage,
    getShowingDescription,
    getShowingName,
    getShowingPoster,
    getShowingPosterBlurDataImage
} from '@cineamo/ui/src/helpers/showing-helper';

/**
 * A showing flattened to exactly what a rendering surface needs, already resolved for one locale.
 *
 * Same rule as `CachedMovieData`, and for the same reason: this is a small, flat set of primitives
 * that a list of showings can hold per row and a server component can serialize. It carries neither
 * the showing nor the movie it was derived from, and no nested cached object — a caller that needs
 * movie fields resolves the movie itself, a caller that needs one more derived value gets one more
 * named primitive field here.
 *
 * This is the one cached type whose every member is a primitive — keep it that way.
 *
 * As with `CachedMovieData`, every image is resolved with `usePlaceholder: false`, so a showing
 * whose movie has no artwork yields nothing rather than a placeholder.
 */
export type CachedShowingData = {
    name: string;
    description: string;
    highlightText?: string;
    ageRating?: string;
    backdropImageURL_ORIGINAL?: string;
    backdropImageURL_W780?: string;
    backdropBlurDataImage?: string;
    posterImageURL_W780?: string;
    posterBlurDataImage?: string;
};

/**
 * Everything a showing renders, resolved against the page locale. Pure — the caller supplies the
 * movie (fetched locale-aware via `/v2/movies/{id}`).
 */
export function getCachedShowingData(
    showing: ShowingDto,
    showingMovie: MovieVersionDto | null | undefined,
    countryCode: CountryCode | undefined,
    locale: string
): CachedShowingData {
    const ageRating = getMovieAgeRating(showingMovie, countryCode);
    const language = getLanguageCodeFromString(locale);

    return {
        name: getShowingName(showing, showingMovie, locale, countryCode),
        highlightText: showing?.highlightText,
        description: getShowingDescription(showing, showingMovie, locale),
        ageRating: ageRating,
        backdropImageURL_ORIGINAL:
            getShowingBackdrop(showing, showingMovie, {
                language,
                countryCode,
                usePosterAsFallback: true,
                usePlaceholder: false,
                movieImageOptions: {
                    resolution: TMDB_IMAGE_RESOLUTION.ORIGINAL
                }
            }) ?? undefined,
        backdropImageURL_W780:
            getShowingBackdrop(showing, showingMovie, {
                language,
                countryCode,
                usePosterAsFallback: true,
                usePlaceholder: false,
                movieImageOptions: {
                    resolution: TMDB_IMAGE_RESOLUTION.W780
                }
            }) ?? undefined,
        backdropBlurDataImage:
            getShowingBackdropBlurDataImage(showing, showingMovie, {
                language,
                countryCode,
                usePosterAsFallback: true,
                usePlaceholder: false
            }) ?? undefined,
        posterImageURL_W780:
            getShowingPoster(showing, showingMovie, {
                language,
                countryCode,
                useBackdropAsFallback: true,
                usePlaceholder: false,
                movieImageOptions: {
                    resolution: TMDB_IMAGE_RESOLUTION.W780
                }
            }) ?? undefined,
        posterBlurDataImage:
            getShowingPosterBlurDataImage(showing, showingMovie, {
                language,
                countryCode,
                useBackdropAsFallback: true,
                usePlaceholder: false
            }) ?? undefined
    };
}
